class BuyOnline {
  constructor(root) {
    this.root = root;
    this.productId = root.dataset.productId || root.dataset.variantId || '';
    this.productHandle = root.dataset.productHandle || '';
    this.endpoint = this.buildEndpoint(root.dataset.endpoint || '');
    this.openBtn = root.querySelector('[data-bo-open-drawer]');
    this.drawer = root.querySelector('[data-bo-drawer]');
    this.overlay = root.querySelector('[data-bo-overlay]');
    this.closeBtn = root.querySelector('[data-bo-close]');
    this.listEl = root.querySelector('[data-bo-list]');
    this.searchInput = root.querySelector('[data-bo-search]');
    this.nearbyBtn = root.querySelector('[data-bo-nearby]');
    this.selectedStoreEl = root.querySelector('[data-bo-selected-store]');
    this.selectedAddressEl = root.querySelector('[data-bo-selected-address]');
    this.selectedPhoneEl = root.querySelector('[data-bo-selected-phone]');
    this.stockWrapper = root.querySelector('.buy-online__stock');
    this.stockEl = root.querySelector('[data-bo-stock]');
    this.pickupStatusEl = root.querySelector('[data-bo-pickup-status]');
    this.shipStatusEl = root.querySelector('[data-bo-ship-status]');
    this.pickupTab = root.querySelector('[data-bo-mode="pickup"]');
    this.shipTab = root.querySelector('[data-bo-mode="ship"]');
    this.bodyEl = root.querySelector('.buy-online__body');
    this.pickupRows = root.querySelectorAll('[data-bo-selected-row]');
    this.selectedMode = 'ship';
    this.locations = [];
    this.filtered = [];
    this.currentSelectionId = null;
    this.userCoordinates = null;
    this.hiddenLocationMap = this.getHiddenLocations();
    this.locationsLoaded = false;
    this.rawLocationData = null; // Store raw API response for online store detection
    this.productAvailable = root.dataset.productAvailable !== 'false'; // Shopify availability fallback
    this.onlineStoreCodes = this.getOnlineStoreCodes();
    this.updateCartAttributes = window.updatePickupCartAttributes;
    this.userSelectedShip = false; // Track if user explicitly selected ship mode
    this.userSelectedPickup = false; // Track if user explicitly selected pickup mode
    this.hasRestoredSelectionState = false;
    this.modeUIRefreshTimer = null;
    this.detachDrawerToBody();
    // Re-query overlay from drawer after it's moved to body
    if (this.drawer) {
      this.overlay = this.drawer.querySelector('[data-bo-overlay]');
    }
    this.attachEvents();
    this.syncSelectedStore();
    this.loadLocations();
    setTimeout(() => {
      if (this.hasRestoredSelectionState) {
        this.updateTabState();
        this.updatePickupRowVisibility();
      }
    }, 1000);
  }

  buildEndpoint(raw) {
    if (!raw) return '';
    let urlString = raw;

    // Replace placeholders
    if (urlString.includes('__HANDLE__')) {
      urlString = urlString.replace(/__HANDLE__/g, this.productHandle || '');
    }
    if (urlString.includes('__PRODUCT_ID__')) {
      urlString = urlString.replace(/__PRODUCT_ID__/g, this.productId || '');
    }

    // Ensure handle/id are present even if placeholders weren't in the setting
    try {
      const url = new URL(urlString);
      if (!url.searchParams.get('handle') && this.productHandle) {
        url.searchParams.set('handle', this.productHandle);
      }
      if (!url.searchParams.get('id') && this.productId) {
        url.searchParams.set('id', this.productId);
      }
      return url.toString();
    } catch (_err) {
      // Fallback for invalid URL strings; append query manually
      const joiner = urlString.includes('?') ? '&' : '?';
      const extra = [];
      if (!urlString.includes('handle=') && this.productHandle) {
        extra.push(`handle=${encodeURIComponent(this.productHandle)}`);
      }
      if (!urlString.includes('id=') && this.productId) {
        extra.push(`id=${encodeURIComponent(this.productId)}`);
      }
      return extra.length ? `${urlString}${joiner}${extra.join('&')}` : urlString;
    }
  }

  detachDrawerToBody() {
    if (!this.drawer || this.drawer.dataset.detachedFromHost) return;
    this.drawer.dataset.detachedFromHost = 'true';
    document.body.appendChild(this.drawer);
    // Re-query overlay from drawer after it's moved to body
    this.overlay = this.drawer.querySelector('[data-bo-overlay]');
  }

  attachEvents() {
    this.openBtn?.addEventListener('click', () => this.openDrawer());
    this.overlay?.addEventListener('click', () => this.closeDrawer());
    this.closeBtn?.addEventListener('click', () => this.closeDrawer());
    this.nearbyBtn?.addEventListener('click', () => this.handleNearby());
    this.pickupTab?.addEventListener('click', () => this.selectMode('pickup'));
    this.shipTab?.addEventListener('click', () => this.selectMode('ship'));
    document.addEventListener('store-selector:change', (event) => {
      if (event.detail?.label) {
        this.setSelectedStore(event.detail.label);
      }
      if (event.detail?.id) {
        this.currentSelectionId = String(event.detail.id);
        this.setSelectedDetailsFromList(this.currentSelectionId);
        this.updateSelectionAvailability();
      }
    });
    
    // Initialize Google Maps autocomplete if API key is available, otherwise use fallback
    this.initSearchInput();
  }

  initSearchInput() {
    if (!this.searchInput) return;
    
    // Check if Google Maps API key is available
    const apiKey = window.inventoryInfo?.settings?.geo?.apiKey;
    
    if (apiKey) {
      // Load Google Maps Places API and initialize autocomplete
      this.initGoogleMapsAutocomplete(apiKey);
    } else {
      // Fall back to existing search functionality
      this.searchInput.addEventListener('input', (e) => this.onSearch(e));
    }
  }

  initGoogleMapsAutocomplete(apiKey) {
    // Check if Google Maps script is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      this.setupAutocomplete();
      return;
    }

    // Load Google Maps Places API script
    const scriptId = 'google-maps-places-api';
    if (document.getElementById(scriptId)) {
      // Script is already loading, wait for it
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkGoogleMaps);
          this.setupAutocomplete();
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.setupAutocomplete();
    };
    script.onerror = () => {
      console.warn('[BuyOnline] Failed to load Google Maps API, falling back to basic search');
      // Fall back to existing search functionality
      this.searchInput.addEventListener('input', (e) => this.onSearch(e));
    };
    document.head.appendChild(script);
  }

  setupAutocomplete() {
    if (!this.searchInput || !window.google || !window.google.maps || !window.google.maps.places) {
      // Fall back to existing search functionality
      this.searchInput.addEventListener('input', (e) => this.onSearch(e));
      return;
    }

    // Create autocomplete instance
    const autocomplete = new window.google.maps.places.Autocomplete(this.searchInput, {
      types: ['address'],
      fields: ['geometry', 'formatted_address', 'name']
    });

    // Store reference to autocomplete for cleanup if needed
    this.autocomplete = autocomplete;

    // Track if a place was selected from autocomplete
    let placeSelected = false;

    // Listen for place selection
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (!place.geometry || !place.geometry.location) {
        console.warn('[BuyOnline] No geometry data for selected place');
        placeSelected = false;
        // Fall back to text search if no geometry
        this.onSearch({ target: this.searchInput });
        return;
      }

      placeSelected = true;

      // Get lat/long from the selected place
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      // Set user coordinates and sort locations by distance
      this.userCoordinates = {
        latitude: this.parseCoord(lat),
        longitude: this.parseCoord(lng),
      };

      // Sort locations by proximity
      this.sortByProximity();
    });

    // Handle manual typing (when user types but doesn't select from autocomplete)
    // Use a debounce to avoid conflicts with autocomplete
    let inputTimeout;
    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(inputTimeout);
      inputTimeout = setTimeout(() => {
        // Only use text search if no place was selected
        if (!placeSelected) {
          this.onSearch(e);
        }
        placeSelected = false; // Reset for next input
      }, 300);
    });

    // Reset placeSelected flag when input is cleared
    this.searchInput.addEventListener('focus', () => {
      placeSelected = false;
    });
  }

  scheduleModeUIRefresh() {
    clearTimeout(this.modeUIRefreshTimer);
    this.modeUIRefreshTimer = setTimeout(() => {
      const applyModeUI = () => {
        this.updateTabState();
        this.updatePickupRowVisibility();
      };

      // Reapply once after async location/init work settles so the visible tab
      // stays aligned with the restored delivery mode.
      applyModeUI();
      setTimeout(applyModeUI, 250);
    }, 0);
  }

  async syncSelectedStore() {
    const api = window.inventoryInfo?.api;
    let loc = null;

    // Restore the shopper's persisted delivery mode first so the PDP respects
    // the cart state instead of always falling back to the ship-first default.
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cartText = await cartRes.text();
      if (cartText.startsWith('{') || cartText.startsWith('[')) {
        const cart = JSON.parse(cartText);
        const attrs = cart?.attributes || {};
        const savedMode = attrs['pickup_mode'];
        const savedLocationId = attrs['pickup_location_id'];
        const savedLocationName = attrs['pickup_location_name'];

        if (savedMode === 'pickup' || savedMode === 'ship') {
          this.selectedMode = savedMode;
          this.userSelectedPickup = savedMode === 'pickup';
          this.userSelectedShip = savedMode === 'ship';
          this.hasRestoredSelectionState = true;
        }

        if (savedLocationId) {
          this.currentSelectionId = String(savedLocationId);
        }

        if (savedLocationName) {
          this.setSelectedStore(savedLocationName);
        }
      }
    } catch (error) {
      console.warn('[BuyOnline] Failed to restore cart selection state', error);
    }

    if (api?.getSelectedLocation) {
      try {
        loc = await api.getSelectedLocation();
      } catch (error) {
        console.warn('[BuyOnline] Failed to sync from API', error);
      }
    }

    // Fallback: read from localStorage if API returned nothing
    if (!loc?.name) {
      try {
        const stored = JSON.parse(localStorage.getItem('storeSelectorSelection'));
        if (!this.currentSelectionId && stored?.id) {
          this.currentSelectionId = String(stored.id);
        }
        if (!this.selectedStoreEl?.textContent?.trim() && stored?.label) {
          this.setSelectedStore(stored.label);
        }
        if (this.currentSelectionId) {
          this.updateSelectionAvailability();
          this.scheduleModeUIRefresh();
          return;
        }
      } catch (e) { /* ignore */ }
      if (this.currentSelectionId || this.selectedMode) {
        this.updateSelectionAvailability();
        this.scheduleModeUIRefresh();
      }
      return;
    }

    this.setSelectedStore(loc.name);
    if (loc?.id !== undefined && loc?.id !== null) {
      this.currentSelectionId = String(loc.id);
    }
    this.setSelectedDetails({
      address1: loc.address1 || loc.address || '',
      city: loc.city || '',
      region: loc.province || loc.state || loc.region || '',
      postal: loc.zip || loc.postal || '',
      phone: loc.phone || '',
    });
    this.updateSelectionAvailability();
    this.scheduleModeUIRefresh();
  }

  setSelectedStore(name) {
    const cleaned = this.stripName(name);
    if (this.selectedStoreEl) this.selectedStoreEl.textContent = cleaned || 'Choose a store';
    if (!name) {
      if (this.selectedAddressEl) this.selectedAddressEl.textContent = '';
      if (this.selectedPhoneEl) this.selectedPhoneEl.textContent = '';
    }
  }

  setSelectedDetails({ address1 = '', city = '', region = '', postal = '', phone = '' } = {}) {
    const addressParts = [address1, [city, region].filter(Boolean).join(', '), postal].filter(
      (p) => p && p.trim()
    );
    if (this.selectedAddressEl) {
      this.selectedAddressEl.textContent = addressParts.join(' · ');
    }
    if (this.selectedPhoneEl) {
      this.selectedPhoneEl.textContent = phone || '';
    }
  }

  stripName(label = '') {
    return label.replace(/^\d+\s*-\s*/, '').replace(/\s*\([^)]*\)\s*/g, '').trim();
  }

  async loadLocations() {
    if (!this.endpoint || !this.listEl) {
      // Initialize empty arrays if endpoint/listEl not available
      this.locations = [];
      this.filtered = [];
      this.locationsLoaded = true;
      this.updateSelectionAvailability();
      return;
    }
    this.listEl.innerHTML = '<div class="bo-drawer__empty">Loading stores…</div>';
    this.locationsLoaded = false;
    try {
      const response = await fetch(this.endpoint, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      // Store raw data for online store detection
      this.rawLocationData = data;
      const normalized = this.normalizeLocations(data) || [];
      this.locations = this.filterHidden(normalized) || [];
      this.mergeExternalCoords();
      this.filtered = this.locations;
      this.locationsLoaded = true;
      this.updateAvailabilityFromLocations();
      this.renderList();
      this.setSelectedDetailsFromList(this.currentSelectionId);
      this.updateSelectionAvailability();
      if (this.hasRestoredSelectionState) {
        this.scheduleModeUIRefresh();
      }
    } catch (error) {
      console.error('[BuyOnline] Failed to load locations', error);
      // Ensure locations is always an array even on error
      this.locations = [];
      this.filtered = [];
      this.locationsLoaded = true;
      this.listEl.innerHTML = '<div class="bo-drawer__empty">Unable to load stores right now.</div>';
      // Still update availability to reflect no pickup availability
      this.updateSelectionAvailability();
    }
  }

  normalizeLocations(data) {
    // Ensure we always return an array
    if (!data) return [];
    // Try variant inventory shape first
    if (Array.isArray(data?.variantLocations) && data.variantLocations.length) {
      const first = data.variantLocations[0];
      const inv = Array.isArray(first?.inventoryLocations) ? first.inventoryLocations : [];
      const parsed = inv
        .map((entry) => {
          const loc = entry?.location || {};
          const id = loc.id ?? loc.locationId ?? loc.location_id;
          if (id == null) return null;
          const name = this.stripName(loc.name || '');
          const city = loc.city || '';
          const region = loc.province || loc.state || loc.region || '';
          const phone = loc.phone || '';
          const address1 = loc.address1 || '';
          const postal = loc.zip || loc.postal || '';
          const quantity = Number(entry.quantity || 0);
          return {
            id,
            name,
            city,
            region,
            phone,
            address1,
            postal,
            quantity,
            latitude: this.parseCoord(loc.latitude),
            longitude: this.parseCoord(loc.longitude),
          };
        })
        .filter(Boolean);
      if (parsed.length) return parsed;
    }

    // Fallback to legacy locations list
    const list = Array.isArray(data?.locations) ? data.locations : Array.isArray(data) ? data : [];
    const result = list
      .map((loc) => {
        const id = loc?.id ?? loc?.locationId ?? loc?.location_id ?? loc?.location?.id;
        if (id == null) return null;
        const name = this.stripName(loc?.name || loc?.location?.name || '');
        const city = loc?.city || loc?.location?.city || '';
        const region = loc?.province || loc?.state || loc?.region || loc?.location?.province || '';
        const phone = loc?.phone || loc?.location?.phone || '';
        const address1 = loc?.address1 || loc?.location?.address1 || '';
        const postal = loc?.zip || loc?.postal || loc?.location?.zip || '';
        const quantity = Number(loc.quantity || 0);
        return {
          id,
          name,
          city,
          region,
          phone,
          address1,
          postal,
          quantity,
          latitude: this.parseCoord(loc.latitude),
          longitude: this.parseCoord(loc.longitude),
        };
      })
      .filter(Boolean);
    
    return result || [];
  }

  parseCoord(value) {
    if (value === null || value === undefined) return null;
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (Number.isNaN(num) || !Number.isFinite(num)) return null;
    return num;
  }

  mergeExternalCoords() {
    if (!Array.isArray(this.locations)) {
      this.locations = [];
      return;
    }
    if (!this.locations.length) return;
    let external = window.storeLocationCoords;
    if (!external) return;
    if (typeof external === 'string') {
      try {
        external = JSON.parse(external);
      } catch (_err) {
        return;
      }
    }

    let sourceList = [];
    if (Array.isArray(external)) {
      sourceList = external;
    } else if (Array.isArray(external?.locations)) {
      sourceList = external.locations;
    } else if (external && typeof external === 'object') {
      Object.values(external).forEach((val) => {
        if (Array.isArray(val)) {
          sourceList = sourceList.concat(val);
        } else {
          sourceList.push(val);
        }
      });
    }

    const coordMap = {};
    sourceList.forEach((item) => {
      const id =
        item?.id ??
        item?.location_id ??
        item?.locationId ??
        item?.location?.id ??
        null;
      if (id === null || id === undefined) return;
      const lat = this.parseCoord(
        item?.latitude ?? item?.lat ?? item?.location?.latitude ?? item?.location?.lat ?? null
      );
      const lng = this.parseCoord(
        item?.longitude ??
          item?.lng ??
          item?.long ??
          item?.lon ??
          item?.location?.longitude ??
          item?.location?.lng ??
          item?.location?.long ??
          item?.location?.lon ??
          null
      );
      coordMap[String(id)] = { latitude: lat, longitude: lng };
    });

    this.locations = this.locations.map((loc) => {
      const coords = coordMap[String(loc.id)];
      if (coords) {
        return {
          ...loc,
          latitude: coords.latitude ?? loc.latitude,
          longitude: coords.longitude ?? loc.longitude,
        };
      }
      return loc;
    });
  }

  renderList() {
    if (!this.listEl) return;
    if (!this.filtered.length) {
      this.listEl.innerHTML = '<div class="bo-drawer__empty">No stores found.</div>';
      return;
    }
    const fragment = document.createDocumentFragment();
    
    // Filter out locations with infinite distance if user coordinates are set
    const locationsToRender = this.filtered.filter((loc) => {
      if (this.userCoordinates && 
          this.userCoordinates.latitude != null && 
          this.userCoordinates.longitude != null &&
          loc.latitude != null && 
          loc.longitude != null) {
        const distanceKm = this.computeDistanceKm(
          this.userCoordinates.latitude,
          this.userCoordinates.longitude,
          loc.latitude,
          loc.longitude
        );
        // Hide locations with infinite distance
        return Number.isFinite(distanceKm);
      }
      // If no user coordinates, show all locations
      return true;
    });
    
    if (!locationsToRender.length) {
      this.listEl.innerHTML = '<div class="bo-drawer__empty">No stores found.</div>';
      return;
    }
    
    locationsToRender.forEach((loc) => {
      // Calculate distance if user coordinates are available
      let distanceHtml = '';
      if (this.userCoordinates && 
          this.userCoordinates.latitude != null && 
          this.userCoordinates.longitude != null &&
          loc.latitude != null && 
          loc.longitude != null) {
        const distanceKm = this.computeDistanceKm(
          this.userCoordinates.latitude,
          this.userCoordinates.longitude,
          loc.latitude,
          loc.longitude
        );
        if (Number.isFinite(distanceKm)) {
          const formattedDistance = this.formatDistance(distanceKm);
          distanceHtml = `<div class="bo-store__distance">${formattedDistance}</div>`;
        }
      }

      const item = document.createElement('article');
      item.className = 'bo-store';
      item.innerHTML = `
        <div class="bo-store__title">${loc.name}</div>
        <div class="bo-store__meta">
          ${distanceHtml}
          <div>${loc.address1 || ''}</div>
          <div>${[loc.city, loc.region].filter(Boolean).join(', ')} ${loc.postal || ''}</div>
          <div>${loc.phone ? `<a href="tel:${loc.phone.replace(/[^0-9+]/g, '')}" class="bo-store__phone">${loc.phone}</a>` : ''}</div>
          <div class="bo-store__qty ${loc.quantity > this.getInventoryThreshold() ? 'is-available' : (loc.quantity > 0 ? 'is-low-stock' : 'is-unavailable')}">
            <span class="bo-store__dot"></span>
            <span>${loc.quantity > this.getInventoryThreshold() ? loc.quantity + ' in stock' : (loc.quantity > 0 ? 'Low stock, please contact store' : 'Out of stock')}</span>
          </div>
        </div>
        <div class="bo-store__actions">
          <button class="bo-store__select" data-id="${loc.id}" type="button">
            <span class="bo-store__select-marker"></span>
            <span>Select this store</span>
          </button>
        </div>
      `;
      const btn = item.querySelector('[data-id]');
      btn?.addEventListener('click', () => this.selectStore(loc));
      fragment.appendChild(item);
    });
    this.listEl.innerHTML = '';
    this.listEl.appendChild(fragment);
  }

  formatDistance(distanceKm) {
    // Format distance: show km for distances >= 1km, meters for < 1km
    if (distanceKm < 1) {
      const meters = Math.round(distanceKm * 1000);
      return `${meters} m`;
    } else if (distanceKm < 10) {
      // Show one decimal place for distances between 1-10km
      return `${distanceKm.toFixed(1)} km`;
    } else {
      // Show whole numbers for distances >= 10km
      return `${Math.round(distanceKm)} km`;
    }
  }

  onSearch(event) {
    // Ensure locations is an array
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }
    const term = (event.target.value || '').toLowerCase();
    if (!term) {
      this.filtered = this.locations;
    } else {
      this.filtered = this.locations.filter((loc) => {
        return (
          loc.name.toLowerCase().includes(term) ||
          loc.city.toLowerCase().includes(term) ||
          loc.region.toLowerCase().includes(term) ||
          (loc.postal || '').toLowerCase().includes(term)
        );
      });
    }
    this.renderList();
  }

  async selectStore(loc) {
    if (!loc || !loc.id) {
      console.warn('[BuyOnline] Invalid location provided to selectStore');
      return;
    }
    const api = window.inventoryInfo?.api;
    if (api?.selectLocation) {
      try {
        await api.selectLocation(loc.id);
        // Persist to localStorage as reliable cross-page storage
        try {
          localStorage.setItem('storeSelectorSelection', JSON.stringify({ id: String(loc.id), label: loc.name || '' }));
        } catch (e) { /* ignore */ }
        this.setSelectedStore(loc.name);
        this.currentSelectionId = String(loc.id);
        this.setSelectedDetails({
          address1: loc.address1 || '',
          city: loc.city || '',
          region: loc.region || '',
          postal: loc.postal || '',
          phone: loc.phone || '',
        });
        this.updateSelectionAvailability();
        this.closeDrawer();
        
        // Update the banner store selector
        this.updateBannerStoreSelector(String(loc.id), loc.name);
        
        // Update cart attributes with correct mode
        this.pushCartAttributes();
        
        document.dispatchEvent(
          new CustomEvent('store-selector:change', { 
            detail: { 
              id: String(loc.id), 
              label: loc.name,
              mode: this.selectedMode || 'ship'
            } 
          })
        );
        return;
      } catch (error) {
        console.warn('[BuyOnline] Failed to select location via API', error);
      }
    }
    // Persist to localStorage even when API is unavailable
    try {
      localStorage.setItem('storeSelectorSelection', JSON.stringify({ id: String(loc.id), label: loc.name || '' }));
    } catch (e) { /* ignore */ }
    this.setSelectedStore(loc.name);
    this.currentSelectionId = String(loc.id);
    this.setSelectedDetails({
      address1: loc.address1 || '',
      city: loc.city || '',
      region: loc.region || '',
      postal: loc.postal || '',
      phone: loc.phone || '',
    });
    this.updateSelectionAvailability();
    this.closeDrawer();
    
    // Update the banner store selector
    this.updateBannerStoreSelector(String(loc.id), loc.name);
    
    // Update cart attributes with correct mode
    this.pushCartAttributes();
  }

  updateBannerStoreSelector(locationId, locationName) {
    // Find the header store selector component
    const headerStoreSelector = document.querySelector('#header-store-selector');
    if (headerStoreSelector && typeof headerStoreSelector.setActiveValue === 'function') {
      // Use the store selector's method to update the banner
      const displayName = this.stripName(locationName);
      headerStoreSelector.setActiveValue(locationId, displayName);
    } else {
      // Fallback: directly update the banner text element
      const bannerValueEl = document.querySelector('.header-store-banner [data-store-selector-value]');
      if (bannerValueEl) {
        const displayName = this.stripName(locationName);
        bannerValueEl.textContent = displayName || locationName;
      }
    }
  }

  updateAvailabilityFromLocations() {
    this.updateSelectionAvailability();
  }

  setStatus(target, isAvailable) {
    if (!target) return;
    const label = target.querySelector('span:last-child');
    if (label) {
      const isPickupStatus = target === this.pickupStatusEl;
      const threshold = this.getInventoryThreshold();
      
      if (isAvailable) {
        target.classList.remove('is-unavailable', 'is-low-stock');
        // For pickup, show "Available at select stores" to encourage checking availability
        if (isPickupStatus) {
          label.textContent = 'Available at select stores';
        } else {
          label.textContent = 'Available';
        }
      } else {
        // Check if this is the pickup status
        let statusText = 'Unavailable';
        let isLowStock = false;
        
        if (isPickupStatus) {
          const hasLocationSelected = this.currentSelectionId && this.currentSelectionId !== '-1';
          
          if (hasLocationSelected) {
            // Location is selected - check that specific location
            const selectedLocation = this.findLocationById(this.currentSelectionId);
            if (selectedLocation && selectedLocation.name) {
              const locationName = this.stripName(selectedLocation.name);
              const qty = Number(selectedLocation.quantity || 0);
              
              // Check if it's low stock (has inventory but at/below threshold)
              if (qty > 0 && qty <= threshold) {
                isLowStock = true;
                statusText = `Low stock at ${locationName}, please contact store for availability`;
              } else {
                statusText = `Unavailable at ${locationName}`;
              }
            }
          } else {
            // No location selected - check if there's any low stock at any pickup location
            const pickupLocations = (this.locations || []).filter(loc => !this.isOnlineStore(loc));
            const hasLowStockAnywhere = pickupLocations.some(loc => {
              const qty = Number(loc.quantity || 0);
              return qty > 0 && qty <= threshold;
            });
            
            if (hasLowStockAnywhere) {
              isLowStock = true;
              statusText = 'Low stock, select store to check availability';
            }
          }
        }
        
        target.classList.toggle('is-unavailable', !isLowStock);
        target.classList.toggle('is-low-stock', isLowStock);
        label.textContent = statusText;
      }
    }
  }

  setSelectedDetailsFromList(id) {
    if (!id) return;
    const loc = this.findLocationById(id);
    if (!loc) return;
    this.setSelectedDetails({
      address1: loc.address1 || '',
      city: loc.city || '',
      region: loc.region || '',
      postal: loc.postal || '',
      phone: loc.phone || '',
    });
  }

  findLocationById(id) {
    if (!id || !Array.isArray(this.locations)) return null;
    return this.locations.find((loc) => String(loc.id) === String(id)) || null;
  }

  getHiddenLocations() {
    const hidden = window.inventoryInfo?.settings?.hiddenLocation;
    if (!hidden || typeof hidden !== 'object') return {};
    const map = {};
    Object.keys(hidden).forEach((key) => {
      const val = hidden[key];
      if (val) {
        map[String(key)] = true;
      }
    });
    return map;
  }

  getOnlineStoreCodes() {
    // Get online store codes from theme settings (no fallback - must be configured)
    const setting = window.FoxTheme?.settings?.onlineStoreLocationCodes || '';
    if (!setting) return [];
    return setting.split(',').map(code => code.trim()).filter(Boolean);
  }

  getInventoryThreshold() {
    return Number(window.FoxTheme?.settings?.bopisInventoryThreshold || 0);
  }

  isOnlineStore(location) {
    if (!location || !location.name) return false;
    const codes = this.onlineStoreCodes || this.getOnlineStoreCodes();
    const name = String(location.name);
    return codes.some(code => name.includes(code));
  }

  findOnlineStore() {
    // First check in parsed locations
    const onlineStore = this.locations.find(loc => this.isOnlineStore(loc));
    if (onlineStore) return onlineStore;
    
    // If not found, check raw data from variantLocations
    if (this.rawLocationData?.variantLocations) {
      const codes = this.onlineStoreCodes || this.getOnlineStoreCodes();
      for (const variant of this.rawLocationData.variantLocations) {
        if (Array.isArray(variant?.inventoryLocations)) {
          for (const entry of variant.inventoryLocations) {
            const loc = entry?.location || {};
            const locName = String(loc.name || '');
            // Check if location name contains any online store code
            if (codes.some(code => locName.includes(code)) && Number(entry.quantity || 0) > this.getInventoryThreshold()) {
              // Return normalized format
              return {
                id: loc.id ?? loc.locationId ?? loc.location_id,
                name: this.stripName(loc.name || ''),
                city: loc.city || '',
                region: loc.province || loc.state || loc.region || '',
                phone: loc.phone || '',
                address1: loc.address1 || '',
                postal: loc.zip || loc.postal || '',
                quantity: Number(entry.quantity || 0),
                latitude: this.parseCoord(loc.latitude),
                longitude: this.parseCoord(loc.longitude),
              };
            }
          }
        }
      }
    }
    return null;
  }

  filterHidden(list) {
    if (!Array.isArray(list)) return [];
    if (!Object.keys(this.hiddenLocationMap || {}).length) {
      return list;
    }
    const filtered = list.filter((loc) => !this.hiddenLocationMap[String(loc.id)]);
    return filtered;
  }

  updateSelectionAvailability() {
    // Ensure locations is always an array
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }
    
    const selected = this.findLocationById(this.currentSelectionId);
    const threshold = this.getInventoryThreshold();
    // Pickup available if selected location has inventory, or any non-online store has inventory
    const pickupLocations = this.locations.filter(loc => !this.isOnlineStore(loc));

    // If the locationInventory app has no data for this product (empty variantLocations),
    // fall back to Shopify's own availability flag — same pattern as buy-buttons.liquid
    const noLocationData = this.locationsLoaded && this.locations.length === 0;
    if (noLocationData && this.productAvailable) {
      const pickupAvailable = true;
      const shipAvailable = true;
      const hasAnyPickupInventory = true;
      this.setStatus(this.pickupStatusEl, pickupAvailable);
      this.setStatus(this.shipStatusEl, shipAvailable);
      if (this.stockEl) this.stockEl.textContent = 'In stock';
      if (this.stockWrapper) {
        this.stockWrapper.classList.remove('is-unavailable');
        this.stockWrapper.classList.add('is-available');
      }
      this.pickupAvailable = pickupAvailable;
      this.shipAvailable = shipAvailable;
      this.hasAnyPickupInventory = hasAnyPickupInventory;
      this.updateTabState();
      this.updatePickupRowVisibility();
      return;
    }

    const pickupAvailable = selected && !this.isOnlineStore(selected)
      ? Number(selected.quantity) > threshold
      : pickupLocations.some((loc) => Number(loc.quantity) > threshold);

    // Track if there's ANY inventory at pickup locations (even below threshold)
    // Used to determine if we should show the "Check store availability" link
    const hasAnyPickupInventory = pickupLocations.some((loc) => Number(loc.quantity) > 0);

    // Ship-to-home follows online store locations (by name pattern, not hardcoded ID)
    const onlineStore = this.findOnlineStore();
    const shipAvailable = onlineStore ? Number(onlineStore.quantity) > threshold : false;

    // Overall availability: either pickup or ship
    const anyAvailable = pickupAvailable || shipAvailable;

    this.setStatus(this.pickupStatusEl, pickupAvailable);
    this.setStatus(this.shipStatusEl, shipAvailable);
    const stockTarget = this.stockWrapper || this.stockEl;
    if (this.stockEl) {
      // Show loading only if locations haven't finished loading
      if (!this.locationsLoaded) {
        this.stockEl.textContent = 'Loading...';
      } else if (anyAvailable) {
        // Show "In stock" if either pickup or ship is available
        this.stockEl.textContent = 'In stock';
      } else {
        // Show "Unavailable" only if both pickup and ship are unavailable
        // If pickup is unavailable and a location is selected, append location name
        let unavailableText = 'Unavailable';
        if (!pickupAvailable && this.currentSelectionId && this.currentSelectionId !== '-1') {
          const selectedLocation = this.findLocationById(this.currentSelectionId);
          if (selectedLocation && selectedLocation.name && !this.isOnlineStore(selectedLocation)) {
            const locationName = this.stripName(selectedLocation.name);
            if (locationName) {
              unavailableText = `Unavailable at ${locationName}`;
            }
          }
        }
        this.stockEl.textContent = unavailableText;
      }
    }
    if (stockTarget) {
      // Update classes based on overall availability
      stockTarget.classList.toggle('is-unavailable', !anyAvailable);
      stockTarget.classList.toggle('is-available', anyAvailable);
    }
    this.pickupAvailable = pickupAvailable;
    this.shipAvailable = shipAvailable;
    this.hasAnyPickupInventory = hasAnyPickupInventory;
    this.updateTabState();
    this.updatePickupRowVisibility();
    
    // Only push cart attributes if we have a meaningful selection and locations are loaded.
    // syncDelivery=false because this is triggered by updateSelectionAvailability (which
    // fires on ALL BuyOnline instances via store-selector:change). Only explicit user
    // actions (selectMode, location pick) should sync the delivery preference.
    if (this.locationsLoaded && this.currentSelectionId && this.currentSelectionId !== '-1') {
      this.pushCartAttributes(false);
    }
  }

  updatePickupRowVisibility() {
    // Check if online location is selected or no location is set
    const selectedLocation = this.findLocationById(this.currentSelectionId);
    const isOnlineLocationSelected = selectedLocation ? this.isOnlineStore(selectedLocation) : false;
    const noLocationSet = !this.currentSelectionId || this.currentSelectionId === null || this.currentSelectionId === '' || this.currentSelectionId === '-1';
    
    // Hide pickup-related rows (store details) when:
    // 1. Pickup is not available above threshold
    // 2. Online location is selected
    // 3. No location is set
    const shouldHidePickupRows = !this.pickupAvailable || 
                                 isOnlineLocationSelected || 
                                 noLocationSet;
    
    if (this.pickupRows && this.pickupRows.length > 0) {
      this.pickupRows.forEach((row) => {
        if (shouldHidePickupRows) {
          row.style.display = 'none';
        } else {
          row.style.display = '';
        }
      });
    }
    
    // Hide the entire body (including CTA link) ONLY if there's truly 0 inventory at all pickup locations
    // If there's low stock (qty > 0 but <= threshold), keep the body visible so users can check store availability
    if (this.bodyEl && this.selectedMode === 'pickup' && !this.hasAnyPickupInventory) {
      this.bodyEl.style.display = 'none';
    } else if (this.bodyEl) {
      this.bodyEl.style.display = '';
    }
  }

  handleNearby() {
    if (!navigator.geolocation) {
      console.warn('[BuyOnline] Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.userCoordinates = {
          latitude: this.parseCoord(pos.coords.latitude),
          longitude: this.parseCoord(pos.coords.longitude),
        };
        this.sortByProximity();
      },
      (err) => {
        console.warn('[BuyOnline] Geolocation error', err);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 }
    );
  }

  sortByProximity() {
    if (!this.userCoordinates || this.userCoordinates.latitude == null || this.userCoordinates.longitude == null)
      return;
    const { latitude: userLat, longitude: userLng } = this.userCoordinates;
    const withDistances = this.locations.map((loc) => ({
      location: loc,
      distance: this.computeDistanceKm(userLat, userLng, loc.latitude, loc.longitude),
    }));
    // Filter out locations with infinite distance
    const known = withDistances.filter((entry) => Number.isFinite(entry.distance));
    known.sort((a, b) => a.distance - b.distance);
    // Only include locations with finite distances
    this.locations = known.map((entry) => entry.location);
    this.filtered = this.locations;
    this.renderList();
  }

  computeDistanceKm(lat1, lon1, lat2, lon2) {
    if (
      lat1 == null ||
      lon1 == null ||
      lat2 == null ||
      lon2 == null ||
      Number.isNaN(lat1) ||
      Number.isNaN(lon1) ||
      Number.isNaN(lat2) ||
      Number.isNaN(lon2)
    ) {
      return Number.POSITIVE_INFINITY;
    }
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  selectMode(mode, force = false) {
    if (mode === 'pickup' && !this.pickupAvailable && !force) return;
    if (mode === 'ship' && !this.shipAvailable && !force) return;
    
    // Track if user explicitly selected a mode (not auto-switched)
    if (!force) {
      if (mode === 'ship') {
        this.userSelectedShip = true;
        this.userSelectedPickup = false;
      } else if (mode === 'pickup') {
        this.userSelectedPickup = true;
        this.userSelectedShip = false;
      }
    }
    
    this.selectedMode = mode;
    this.updateTabState();
    this.updatePickupRowVisibility();
    this.sampleProductwithLocation();
    this.pushCartAttributes();
  }

  updateTabState() {
    const pickupTab = this.pickupTab;
    const shipTab = this.shipTab;
    const pickupActive = this.selectedMode === 'pickup';
    const shipActive = this.selectedMode === 'ship';
    if (pickupTab) {
      pickupTab.classList.toggle('is-active', pickupActive);
      pickupTab.setAttribute('aria-selected', pickupActive ? 'true' : 'false');
      pickupTab.classList.toggle('is-unavailable', !this.pickupAvailable);
      pickupTab.setAttribute('aria-disabled', this.pickupAvailable ? 'false' : 'true');
    }
    if (shipTab) {
      shipTab.classList.toggle('is-active', shipActive);
      shipTab.setAttribute('aria-selected', shipActive ? 'true' : 'false');
      shipTab.classList.toggle('is-unavailable', !this.shipAvailable);
      shipTab.setAttribute('aria-disabled', this.shipAvailable ? 'false' : 'true');
    }

    // Auto-switch if current mode is unavailable but the other is available
    // Only auto-switch after locations are loaded to avoid premature switching
    if (this.locationsLoaded) {
      // Prefer ship if available (unless user explicitly selected pickup)
      if (this.shipAvailable && !this.userSelectedPickup && this.selectedMode !== 'ship') {
        this.selectMode('ship', true);
      } else if (this.selectedMode === 'ship' && !this.shipAvailable && this.pickupAvailable) {
        // Only auto-switch to pickup if ship is not available
        this.selectMode('pickup', true);
      } else if (this.selectedMode === 'pickup' && !this.pickupAvailable && this.shipAvailable) {
        // Switch back to ship if pickup becomes unavailable and ship is available
        if (!this.userSelectedPickup) {
          this.selectMode('ship', true);
        }
      }
    }
  }

  sampleProductwithLocation() {
    const loc =
      this.selectedMode === 'ship'
        ? this.findOnlineStore() || this.findLocationById(this.currentSelectionId)
        : this.findLocationById(this.currentSelectionId);
  }

  pushCartAttributes(syncDelivery = true) {
    if (typeof this.updateCartAttributes !== 'function') return;
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }

    // For ship mode, don't pass any location - clear it
    // For pickup mode, use the currently selected location
    const loc = this.selectedMode === 'pickup'
      ? this.findLocationById(this.currentSelectionId)
      : null;

    const payload = {
      mode: this.selectedMode || '',
      id: loc?.id != null ? String(loc.id) : '',
      name: loc?.name || '',
      syncDelivery,
    };

    const key = payload.mode + '|' + payload.id;
    if (key === this._lastPushedKey && !syncDelivery) return;
    this._lastPushedKey = key;

    this.updateCartAttributes(payload);
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.removeAttribute('hidden');
    document.documentElement.classList.add('bo-drawer-open');
    this.searchInput?.focus();
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.hidden = true;
    document.documentElement.classList.remove('bo-drawer-open');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-buy-online]').forEach((el) => {
    const instance = new BuyOnline(el);
    // Store instance on element for external access
    el.__buyOnlineInstance = instance;
  });
});

// ─── Storefront API: Sync delivery preference to prefill checkout ────────────
const CART_BUYER_IDENTITY_MUTATION = `
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        id
        checkoutUrl
        buyerIdentity {
          countryCode
          preferences {
            delivery {
              deliveryMethod
              pickupHandle
            }
          }
        }
      }
      userErrors { field message }
    }
  }
`;

/**
 * Reads the full cart token (including ?key=) from the 'cart' cookie.
 * The key param is required for Storefront API cart mutations.
 * @returns {string|null} The full cart cookie value, or null if not found.
 */
function getCartTokenFromCookie() {
  const match = document.cookie.split('; ').find((c) => c.startsWith('cart='));
  if (!match) return null;
  return decodeURIComponent(match.split('=').slice(1).join('='));
}

/**
 * Calls cartBuyerIdentityUpdate via the Storefront API to prefill checkout
 * with the correct delivery method (PICK_UP or SHIPPING).
 *
 * This is non-blocking — if it fails, the user still checks out normally.
 * The preference is set on the cart object itself, so it works regardless of
 * how checkout is triggered (Rebuy Smart Cart, native form, direct URL).
 *
 * Uses the cart cookie (which includes the ?key= param) to build the full
 * Storefront API cart GID. The key is required for mutations per Shopify's
 * August 2024 policy change.
 *
 * @param {string} _cartToken - Unused (kept for backwards compat), reads from cookie instead
 * @param {string} mode - "pickup" or "ship" or ""
 * @param {string} locationId - The location's legacyResourceId (pickup handle)
 * @returns {Promise<boolean>} - true if successful, false otherwise
 */
window.syncDeliveryPreference = async function syncDeliveryPreference(_cartToken, mode, locationId) {
  const storefrontToken = window.FoxTheme?.settings?.storefrontAccessToken;
  const cartCookieValue = getCartTokenFromCookie();

  if (!storefrontToken || !cartCookieValue) {
    console.warn('[syncDeliveryPreference] Missing storefrontToken or cart cookie, skipping.');
    return false;
  }

  const API_VERSION = '2025-01';
  const cartId = `gid://shopify/Cart/${cartCookieValue}`;
  const endpoint = `/api/${API_VERSION}/graphql.json`;
  const isPickup = mode === 'pickup';
  const hasPickupHandle = isPickup && locationId;

  const variables = {
    cartId,
    buyerIdentity: {
      countryCode: 'CA',
      preferences: {
        delivery: {
          deliveryMethod: [isPickup ? 'PICK_UP' : 'SHIPPING'],
          pickupHandle: hasPickupHandle ? [String(locationId)] : []
        }
      }
    }
  };


  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken
      },
      body: JSON.stringify({ query: CART_BUYER_IDENTITY_MUTATION, variables })
    });

    const data = await res.json();
    const payload = data.data?.cartBuyerIdentityUpdate;

    if (payload?.userErrors?.length) {
      return null;
    }

    const checkoutUrl = payload?.cart?.checkoutUrl;


    // Return the checkoutUrl from Shopify which includes cart key and preserves buyer identity
    return checkoutUrl || null;
  } catch (err) {
    console.warn('[syncDeliveryPreference] Failed:', err);
    return null;
  }
};

// ─── Resilient fetch helper (retries on 429 / Cloudflare challenge) ──────────
async function _resilientFetch(url, options, retries = 3) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, options);
      if (res.status === 429 || (res.status >= 500 && res.status < 600)) {
        if (attempt >= retries) return res;
        const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('json') && ct.includes('html')) {
        if (attempt >= retries) return res;
        const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
      return res;
    } catch (err) {
      if (attempt >= retries) throw err;
      const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
}

// ─── Shared helper to persist selection into cart attributes ─────────────────
// Debounced (500ms) to coalesce rapid calls from multiple components.
// Calls syncDeliveryPreference after attribute updates to sync the delivery
// method (PICK_UP / SHIPPING) to the Storefront API for checkout prefill.
let _cartUpdatePending = null;
let _cartUpdateTimer = null;
let _cartUpdateInFlight = false;

window.updatePickupCartAttributes = function updatePickupCartAttributes(selection = {}) {
  _cartUpdatePending = { ..._cartUpdatePending, ...selection };
  clearTimeout(_cartUpdateTimer);

  if (_cartUpdateInFlight) return;

  _cartUpdateTimer = setTimeout(() => {
    const payload = _cartUpdatePending;
    _cartUpdatePending = null;
    _executeCartAttributeUpdate(payload);
  }, 500);
};

// Sync delivery preference and restore any cart items removed by the
// Storefront API cartBuyerIdentityUpdate mutation (e.g. gift wrap).
async function _syncDeliveryAndRestore(currentCart, mode, id) {
  const itemsBefore = (currentCart?.items || []).map(item => ({
    id: item.variant_id || item.id,
    quantity: item.quantity,
    properties: item.properties || {},
  }));

  await window.syncDeliveryPreference(null, mode, id);

  if (itemsBefore.length > 0) {
    try {
      const afterRes = await _resilientFetch('/cart.js');
      if (afterRes && afterRes.ok) {
        const afterCart = await afterRes.json();
        const itemsAfterSet = new Set(
          (afterCart?.items || []).map(item => String(item.variant_id || item.id))
        );
        const removedItems = itemsBefore.filter(item => !itemsAfterSet.has(String(item.id)));
        if (removedItems.length > 0) {
          await _resilientFetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: removedItems.map(item => ({
                id: item.id,
                quantity: item.quantity,
                properties: item.properties,
              })),
            }),
          });
        }
      }
    } catch (restoreErr) {
      console.warn('[updatePickupCartAttributes] Failed to restore items after delivery sync', restoreErr);
    }
  }
}

async function _executeCartAttributeUpdate(selection) {
  if (!selection) return;
  _cartUpdateInFlight = true;
  try {
    const mode = selection.mode || '';
    const id = selection.id || '';
    const name = selection.name || '';
    const clearAll = selection.clearAll === true;
    const shouldSyncDelivery = selection.syncDelivery !== false;

    if (clearAll) {
      try { localStorage.removeItem('storeSelectorSelection'); } catch (e) { /* ignore */ }
      const cartRes = await _resilientFetch('/cart.js');
      if (!cartRes || !cartRes.ok) return;
      const currentCart = await cartRes.json();
      const attributes = { ...(currentCart?.attributes || {}) };

      attributes['pickup_mode'] = '';
      attributes['pickup_location_id'] = '';
      attributes['pickup_location_name'] = '';

      await _resilientFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });

      if (shouldSyncDelivery) {
        await _syncDeliveryAndRestore(currentCart, '', '');
      }
      return;
    }

    if (!mode && !id && !name) return;

    const cartRes = await _resilientFetch('/cart.js');
    if (!cartRes || !cartRes.ok) return;
    const currentCart = await cartRes.json();
    const attributes = { ...(currentCart?.attributes || {}) };

    // Always set these attributes (even if empty) to clear old values when switching modes
    if (mode !== undefined) attributes['pickup_mode'] = mode;
    if (id !== undefined) attributes['pickup_location_id'] = id;
    if (name !== undefined) attributes['pickup_location_name'] = name;

    await _resilientFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });

    if (shouldSyncDelivery) {
      await _syncDeliveryAndRestore(currentCart, mode, id);
    }
  } catch (error) {
    console.warn('[updatePickupCartAttributes] Failed to update cart attributes', error);
  } finally {
    _cartUpdateInFlight = false;
    if (_cartUpdatePending) {
      const next = _cartUpdatePending;
      _cartUpdatePending = null;
      _executeCartAttributeUpdate(next);
    }
  }
}
// ─── Re-sync delivery preference right before checkout ──────────────────────
// The cartBuyerIdentityUpdate preference is set when the user clicks a tab or
// selects a store, but adding/removing cart items can reset it. This interceptor
// re-syncs the preference when the customer clicks checkout.
(function setupCheckoutSync() {

  // Capture-phase listener on document to intercept before Rebuy handlers
  document.addEventListener('click', function(e) {
    var target = e.target;

    // Walk up to find a link or button
    while (target && target !== document) {
      if (target.tagName === 'A' || target.tagName === 'BUTTON') {
        var href = target.getAttribute('href') || '';
        var text = (target.textContent || '').toLowerCase();

        // Check if this is a checkout link/button
        if (href.includes('/checkout') || text.includes('checkout')) {
          // Check if this is inside the Rebuy cart
          var rebuyCart = document.getElementById('rebuy-cart');
          if (rebuyCart && rebuyCart.contains(target)) {

            e.preventDefault();
            e.stopImmediatePropagation();

            // Flush any pending debounced cart updates
            if (_cartUpdateTimer) {
              clearTimeout(_cartUpdateTimer);
              var pending = _cartUpdatePending;
              _cartUpdatePending = null;
              if (pending && !_cartUpdateInFlight) {
                _executeCartAttributeUpdate(pending);
              }
            }

            // Wait a moment for flush to complete
            setTimeout(function() {
              var savedItems = [];
              fetch('/cart.js')
                .then(function(r) { return r.json(); })
                .then(function(cart) {
                  var attrs = (cart && cart.attributes) || {};
                  var mode = attrs['pickup_mode'] || '';
                  var locationId = attrs['pickup_location_id'] || '';
                  var locationName = attrs['pickup_location_name'] || '';

                  // Save cart items before sync — cartBuyerIdentityUpdate can remove items
                  savedItems = (cart.items || []).map(function(item) {
                    return { id: item.variant_id || item.id, quantity: item.quantity, properties: item.properties || {} };
                  });

                  // If pickup mode with a location selected, validate cart items
                  if (mode === 'pickup' && locationId && window.inventoryInfo?.api?.selectLocation) {

                    return window.inventoryInfo.api.selectLocation(locationId)
                      .then(function() {
                        return window.syncDeliveryPreference(null, 'pickup', locationId);
                      })
                      .catch(function(error) {
                        if (error?.status === 422) {
                          console.warn('[CheckoutSync] ✗ Items not available at ' + (locationName || 'selected location') + '. Defaulting to shipping.');
                          return window.syncDeliveryPreference(null, 'ship', '');
                        }
                        console.warn('[CheckoutSync] Validation error, proceeding with original mode:', error);
                        return window.syncDeliveryPreference(null, mode, locationId);
                      });
                  }

                  // No pickup mode or no location - sync as-is
                  return window.syncDeliveryPreference(null, mode, locationId);
                })
                .then(function(checkoutUrl) {
                  // Restore any items removed by the delivery sync before checkout
                  if (savedItems.length > 0) {
                    return fetch('/cart.js')
                      .then(function(r) { return r.json(); })
                      .then(function(afterCart) {
                        var afterIds = {};
                        (afterCart.items || []).forEach(function(item) {
                          afterIds[String(item.variant_id || item.id)] = true;
                        });
                        var removed = savedItems.filter(function(item) {
                          return !afterIds[String(item.id)];
                        });
                        if (removed.length > 0) {
                          return fetch('/cart/add.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                            body: JSON.stringify({
                              items: removed.map(function(item) {
                                return { id: item.id, quantity: item.quantity, properties: item.properties };
                              }),
                            }),
                          }).then(function() { return checkoutUrl; });
                        }
                        return checkoutUrl;
                      });
                  }
                  return checkoutUrl;
                })
                .then(function(checkoutUrl) {
                  // Use fresh checkoutUrl from API if available to ensure new checkout session
                  // Otherwise fall back to /checkout
                  window.location.href = checkoutUrl || '/checkout';
                })
                .catch(function(err) {
                  console.warn('[CheckoutSync] Sync failed, proceeding to checkout anyway:', err);
                  window.location.href = '/checkout';
                });
            }, 100);

            return false;
          }
        }
      }
      target = target.parentElement;
    }
  }, true); // Use capture phase
})();
